import { NgFor, NgIf } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService } from '../../core/api.service';
import { LocalDatePipe } from '../../core/date-utils';
import { OcorrenciaResolucao, OcorrenciaRetirada } from '../../core/models';

@Component({
  standalone: true,
  imports: [ReactiveFormsModule, NgFor, NgIf, RouterLink, LocalDatePipe],
  template: `
    <header class="page-header">
      <div><h2>Ocorrencias</h2><p>Divergencias geradas nas retiradas</p></div>
    </header>

    <div class="toast-stack" *ngIf="erro || mensagem">
      <div class="toast" [class.toast-error]="erro" [class.toast-success]="!erro && mensagem" role="alert">
        <span>{{ erro || mensagem }}</span>
        <button class="toast-close" type="button" (click)="fecharAviso()" aria-label="Fechar mensagem">X</button>
      </div>
    </div>

    <div class="modal-backdrop" *ngIf="resolucao">
      <section class="modal" role="dialog" aria-modal="true" aria-labelledby="resolver-ocorrencia-title">
        <header class="modal-header">
          <div>
            <h3 id="resolver-ocorrencia-title">Resolver ocorrencia {{ resolucao.ocorrencia.id_ocorrencia }}</h3>
            <p>{{ resolucao.ocorrencia.codigo_produto }} - {{ resolucao.ocorrencia.produto }} | Tam. {{ resolucao.ocorrencia.tamanho }}</p>
          </div>
          <button class="ghost modal-close" type="button" (click)="fecharResolucao()" aria-label="Fechar">X</button>
        </header>

        <section class="resolution-summary">
          <article><span>{{ quantidadeOrigemLabel(resolucao.ocorrencia) }}</span><strong>{{ resolucao.ocorrencia.quantidade_retirada }}</strong></article>
          <article><span>{{ saldoOrigemLabel(resolucao.ocorrencia) }}</span><strong>{{ resolucao.ocorrencia.saldo_conferido }}</strong></article>
          <article><span>Divergencia</span><strong>{{ resolucao.ocorrencia.quantidade_divergente }}</strong></article>
        </section>

        <h4>Conferencia</h4>
        <table class="size-table">
          <thead><tr><th>Codigo</th><th>Data</th><th>Faccao</th><th>Status</th><th>Quantidade</th></tr></thead>
          <tbody>
            <tr *ngFor="let conferencia of resolucao.conferencias">
              <td data-label="Codigo">{{ conferencia.id_conferencia }}</td>
              <td data-label="Data">{{ conferencia.data_conferencia | localDate }}</td>
              <td data-label="Faccao">{{ conferencia.faccao }}</td>
              <td data-label="Status">{{ conferencia.status }}</td>
              <td data-label="Quantidade">
                <input type="number" min="0" [value]="conferencia.quantidade" (input)="conferencia.quantidade = numberValue($any($event.target).value)">
              </td>
            </tr>
          </tbody>
        </table>
        <p class="field-hint" *ngIf="!resolucao.conferencias.length">Nenhuma conferencia encontrada para este produto e tamanho</p>

        <h4>Contagem</h4>
        <table class="size-table">
          <thead><tr><th>Codigo</th><th>Data</th><th>Responsaveis</th><th>Status</th><th>Quantidade</th><th>Defeitos</th><th>Total</th></tr></thead>
          <tbody>
            <tr *ngFor="let contagem of resolucao.contagens">
              <td data-label="Codigo">{{ contagem.id_contagem }}</td>
              <td data-label="Data">{{ contagem.data_contagem | localDate }}</td>
              <td data-label="Responsaveis">{{ contagem.responsaveis }}</td>
              <td data-label="Status">{{ contagem.status }}</td>
              <td data-label="Quantidade">
                <input type="number" min="0" [value]="contagem.quantidade" (input)="contagem.quantidade = numberValue($any($event.target).value)">
              </td>
              <td data-label="Defeitos">{{ contagem.defeitos }}</td>
              <td data-label="Total">{{ totalContagem(contagem) }}</td>
            </tr>
          </tbody>
        </table>
        <p class="field-hint" *ngIf="!resolucao.contagens.length">Nenhuma contagem encontrada para este produto e tamanho</p>

        <form [formGroup]="resolucaoForm" novalidate>
          <label class="full">Observacao<textarea rows="3" formControlName="observacao"></textarea></label>
        </form>

        <div class="form-actions">
          <button type="button" (click)="salvarResolucao()">Salvar e resolver</button>
          <button class="ghost" type="button" (click)="fecharResolucao()">Cancelar</button>
        </div>
      </section>
    </div>

    <section class="panel">
      <form [formGroup]="filtrosForm" (ngSubmit)="carregar()" novalidate>
        <div class="grid-form">
          <label>Status
            <select formControlName="status">
              <option value="">Todos</option>
              <option value="ABERTA">Aberta</option>
              <option value="RESOLVIDA">Resolvida</option>
              <option value="CANCELADA">Cancelada</option>
            </select>
          </label>
          <label>Ordem<input formControlName="numero_nota" placeholder="OE-000001"></label>
        </div>
        <div class="form-actions">
          <button type="submit">Filtrar</button>
          <button class="ghost" type="button" (click)="limparFiltros()">Limpar</button>
        </div>
      </form>
    </section>

    <section class="panel">
      <table class="data-table">
        <thead>
          <tr>
            <th>Codigo</th>
            <th>Ordem</th>
            <th>Origem</th>
            <th>Retirada</th>
            <th>Cliente</th>
            <th>Produto</th>
            <th>Tamanho</th>
            <th>Retirado</th>
            <th>Saldo</th>
            <th>Divergencia</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let ocorrencia of ocorrencias">
            <td data-label="Codigo">{{ ocorrencia.id_ocorrencia }}</td>
            <td data-label="Ordem"><a [routerLink]="['/notas', ocorrencia.id_nota]">{{ ocorrencia.numero_nota }}</a></td>
            <td data-label="Origem">{{ origemLabel(ocorrencia) }}</td>
            <td data-label="Retirada">{{ ocorrencia.id_retirada || '-' }}</td>
            <td data-label="Cliente">{{ ocorrencia.cliente }}</td>
            <td data-label="Produto">{{ ocorrencia.codigo_produto }} - {{ ocorrencia.produto }}</td>
            <td data-label="Tamanho">{{ ocorrencia.tamanho }}</td>
            <td data-label="Retirado">{{ ocorrencia.quantidade_retirada }}</td>
            <td data-label="Saldo">{{ ocorrencia.saldo_conferido }}</td>
            <td data-label="Divergencia">{{ ocorrencia.quantidade_divergente }}</td>
            <td data-label="Status">
              <span class="status" [class.finalizada]="ocorrencia.status === 'RESOLVIDA'" [class.cancelada]="ocorrencia.status === 'CANCELADA'">
                {{ statusLabel(ocorrencia.status) }}
              </span>
            </td>
            <td class="actions" data-label="Acoes">
              <button type="button" (click)="abrirResolucao(ocorrencia)" *ngIf="ocorrencia.status === 'ABERTA'">Resolver</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p class="field-hint" *ngIf="!ocorrencias.length">Nenhuma ocorrencia encontrada</p>
    </section>
  `
})
export class OcorrenciasComponent implements OnInit {
  private api = inject(ApiService);
  private fb = inject(FormBuilder);

  ocorrencias: OcorrenciaRetirada[] = [];
  resolucao?: OcorrenciaResolucao;
  erro = '';
  mensagem = '';

  filtrosForm = this.fb.nonNullable.group({
    status: ['ABERTA'],
    numero_nota: ['']
  });

  resolucaoForm = this.fb.nonNullable.group({
    observacao: ['']
  });

  ngOnInit() {
    this.carregar();
  }

  carregar() {
    this.erro = '';
    this.mensagem = '';
    this.api.ocorrencias(this.filtrosForm.getRawValue()).subscribe({
      next: (ocorrencias) => (this.ocorrencias = ocorrencias),
      error: (error) => (this.erro = error.error?.message || 'Erro ao carregar ocorrencias')
    });
  }

  limparFiltros() {
    this.filtrosForm.reset({ status: '', numero_nota: '' });
    this.carregar();
  }

  abrirResolucao(ocorrencia: OcorrenciaRetirada) {
    this.erro = '';
    this.mensagem = '';
    this.api.ocorrenciaResolucao(ocorrencia.id_ocorrencia).subscribe({
      next: (resolucao) => {
        this.resolucao = resolucao;
        this.resolucaoForm.reset({ observacao: resolucao.ocorrencia.observacao || '' });
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao carregar dados da ocorrencia')
    });
  }

  salvarResolucao() {
    if (!this.resolucao) return;

    this.erro = '';
    this.mensagem = '';
    const payload = {
      observacao: this.resolucaoForm.getRawValue().observacao,
      conferencias: this.resolucao.conferencias.map((item) => ({
        id_item: item.id_item,
        quantidade: Number(item.quantidade || 0)
      })),
      contagens: this.resolucao.contagens.map((item) => ({
        id_item: item.id_item,
        quantidade: Number(item.quantidade || 0)
      }))
    };

    this.api.resolverOcorrencia(this.resolucao.ocorrencia.id_ocorrencia, payload).subscribe({
      next: () => {
        this.resolucao = undefined;
        this.mensagem = 'Ocorrencia resolvida com sucesso';
        this.carregar();
      },
      error: (error) => (this.erro = error.error?.message || 'Erro ao salvar resolucao')
    });
  }

  fecharResolucao() {
    this.resolucao = undefined;
  }

  numberValue(value: string) {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? Math.trunc(parsed) : 0;
  }

  totalContagem(contagem: { quantidade: number; defeitos: number }) {
    return Number(contagem.quantidade || 0) + Number(contagem.defeitos || 0);
  }

  origemLabel(ocorrencia: OcorrenciaRetirada) {
    return ocorrencia.origem === 'FINALIZACAO_ORDEM' ? 'Finalizacao' : 'Retirada';
  }

  quantidadeOrigemLabel(ocorrencia: OcorrenciaRetirada) {
    return ocorrencia.origem === 'FINALIZACAO_ORDEM' ? 'Contado' : 'Retirado';
  }

  saldoOrigemLabel(ocorrencia: OcorrenciaRetirada) {
    return ocorrencia.origem === 'FINALIZACAO_ORDEM' ? 'Conferido' : 'Saldo conferido';
  }

  fecharAviso() {
    this.erro = '';
    this.mensagem = '';
  }

  statusLabel(status: string) {
    return {
      ABERTA: 'Aberta',
      RESOLVIDA: 'Resolvida',
      CANCELADA: 'Cancelada'
    }[status] || status;
  }
}
